import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { EditableText } from "./live/Editable";

export default function FacultySection() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/faculty", { params: { active_only: true } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const principal = items.find((f) => (f.role || "").toLowerCase().includes("principal"));
  const rest = items.filter((f) => f !== principal);

  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden" data-testid="home-faculty">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-yellow-100 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <EditableText path="home.faculty.kicker" as="p" className="kicker mb-3">
            Meet our team
          </EditableText>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0B2A6B] leading-tight">
            <EditableText path="home.faculty.heading" as="span">
              German experts & Indian specialists
            </EditableText>
          </h2>
          <EditableText path="home.faculty.subtitle" as="p" multiline className="text-slate-600 mt-4">
            Our curriculum is designed by German experts and delivered by highly trained faculty with great depth of experience.
          </EditableText>
        </div>

        {/* Featured principal */}
        {principal && (
          <div
            className="mb-12 grid md:grid-cols-[300px_1fr] gap-0 rounded-3xl overflow-hidden border border-slate-100 shadow-lg bg-white"
            data-testid="home-faculty-principal"
          >
            <div className="aspect-[4/5] md:aspect-auto md:h-full overflow-hidden bg-slate-100">
              <img src={principal.image_url} alt={principal.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-8 lg:p-10 flex flex-col justify-center bg-[#0B2A6B] text-white">
              <span className="inline-block w-fit px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[11px] font-bold uppercase tracking-wider mb-4">
                {principal.role}
              </span>
              <h3 className="text-2xl lg:text-3xl font-bold mb-3">{principal.name}</h3>
              <p className="text-slate-200 leading-relaxed">{principal.bio}</p>
            </div>
          </div>
        )}

        {/* Trainers grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {rest.map((f, i) => (
            <div
              key={f.id}
              className="group relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl border border-slate-100 tilt-card animate-fade-up"
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className="aspect-square overflow-hidden bg-slate-100">
                <img
                  src={f.image_url || "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400"}
                  alt={f.name}
                  className="w-full h-full object-cover zoom-img"
                />
              </div>
              <div className="p-4 text-center">
                <h3 className="font-bold text-[#0B2A6B] text-sm leading-tight">{f.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{f.role}</p>
              </div>
              <div className="absolute inset-0 bg-[#0B2A6B]/95 text-white p-4 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition">
                <h3 className="font-bold text-yellow-400 text-sm">{f.name}</h3>
                <p className="text-[11px] text-yellow-200 mb-2">{f.role}</p>
                <p className="text-xs leading-relaxed line-clamp-5">{f.bio}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/faculty"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-[#0B2A6B] text-white font-semibold hover:bg-[#061a47] transition group"
          >
            Meet the full team
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
          </Link>
        </div>
      </div>
    </section>
  );
}
